<template>
  <div class="app-layout">
    <AppHeader
      :search="search"
      :activeCount="activeCount"
      :screen="screen"
      :mode="mode"
      :userName="auth.userName.value"
      :companyName="auth.companyName.value"
      @search="handleSearch"
      @go-orders="goOrders"
      @go-cliente="goCliente"
      @logout="handleLogout"
      @update:mode="mode = $event"
    />

    <div class="app-content" :class="{ 'app-content--overflow-hidden': screen === 'pedido' }">
      <ClientePicker
        v-if="screen === 'cliente'"
        :activeIds="activeIds"
        :store="store"
        @update:activeIds="activeIds = $event"
        @continue="goPedido"
      />

      <PedidoScreen
        v-else-if="screen === 'pedido'"
        :activeIds="activeIds"
        :cart="cart"
        :notes="notes"
        :search="search"
        :category="category"
        :mode="mode"
        :focused="focused"
        :mutedIds="mutedIds"
        :expanded="expanded"
        :store="store"
        @update:cart="cart = $event"
        @update:notes="notes = $event"
        @update:category="category = $event"
        @update:mode="mode = $event"
        @update:focused="focused = $event"
        @update:muted="toggleMuted"
        @update:expanded="expanded = $event"
        @checkout="goCheckout"
      />

      <Checkout
        v-else-if="screen === 'checkout'"
        :activeIds="activeIds"
        :cart="cart"
        :notes="notes"
        :store="store"
        :auth="auth"
        @back="goPedido"
        @confirm="goConfirmacion"
      />

      <Confirmation
        v-else-if="screen === 'confirmacion'"
        :activeIds="activeIds"
        :cart="cart"
        :store="store"
        @back-home="goOrders"
        @new-order="newOrder"
      />

      <OrdersList
        v-else-if="screen === 'ordenes'"
        :selected="selectedOrder"
        :store="store"
        @update:selected="selectedOrder = $event"
        @back="goPedido"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import AppHeader   from './AppHeader.vue'
import ClientePicker from './ClientePicker.vue'
import PedidoScreen  from './PedidoScreen.vue'
import Checkout      from './Checkout.vue'
import Confirmation  from './Confirmation.vue'
import OrdersList    from './OrdersList.vue'

const props = defineProps({
  initialScreen: { type: String, default: 'cliente' },
  initialMode:   { type: String, default: 'tarjetas' },
  initialActive: { type: Array,  default: () => [] },
  initialCart:   { type: Object, default: () => ({}) },
  store:         { type: Object, required: true },
  auth:          { type: Object, required: true },
})

// ── Session persistence ───────────────────────────────────────────────────────
const SESSION_KEY = 'sicamex_session'

function loadSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null') || {} }
  catch { return {} }
}
function saveSession(s) {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)) }
  catch {}
}
function clearSession() {
  localStorage.removeItem(SESSION_KEY)
}

const saved = loadSession()
const restorableScreens = new Set(['pedido', 'checkout'])

const screen    = ref(restorableScreens.has(saved.screen) ? saved.screen : props.initialScreen)
const search    = ref('')
const category  = ref(saved.category  || 'all')
const mode      = ref(saved.mode      || props.initialMode)
const activeIds = ref(saved.activeIds || [...props.initialActive])
const focused   = ref(saved.focused   || props.initialActive[0] || null)
const mutedIds  = ref(saved.mutedIds  || [])
const cart      = ref(saved.cart      || { ...props.initialCart })
const notes     = ref(saved.notes     || {})
const expanded  = ref({})
const selectedOrder = ref(null)

watch(
  [screen, category, mode, activeIds, focused, mutedIds, cart, notes],
  () => saveSession({
    screen:    screen.value,
    category:  category.value,
    mode:      mode.value,
    activeIds: activeIds.value,
    focused:   focused.value,
    mutedIds:  mutedIds.value,
    cart:      cart.value,
    notes:     notes.value,
  }),
  { deep: true }
)

const activeCount = computed(() =>
  activeIds.value.filter(id => !mutedIds.value.includes(id)).length
)

watch(activeIds, (ids) => {
  if (!ids.includes(focused.value)) focused.value = ids[0] || null
})

function toggleMuted(comedorId) {
  const idx = mutedIds.value.indexOf(comedorId)
  if (idx >= 0) mutedIds.value.splice(idx, 1)
  else mutedIds.value.push(comedorId)
}

function handleSearch(value) {
  search.value = value
  if (value && screen.value === 'cliente') goPedido()
}

function handleLogout() {
  props.auth.logout()
  window.location.reload()
}

const goPedido       = () => { screen.value = 'pedido' }
const goCliente      = () => { screen.value = 'cliente' }
const goCheckout     = () => { screen.value = 'checkout' }
const goConfirmacion = () => { screen.value = 'confirmacion' }
const goOrders       = () => { screen.value = 'ordenes' }

function newOrder() {
  cart.value      = {}
  notes.value     = {}
  mutedIds.value  = []
  activeIds.value = []
  screen.value    = 'cliente'
  clearSession()
}
</script>

<style scoped>
.app-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  font-family: var(--ff-body);
  color: var(--text);
  background: var(--bg);
}

.app-content {
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

.app-content--overflow-hidden {
  overflow: hidden;
}
</style>
